/**
=========================================================
* Trust Cargo - the notification bell
=========================================================

* The bell in the top bar: how many things are waiting, and what they are,
* without leaving the page you are on.
*
* Two different pieces of state, and they are not the same thing:
*
*   done    - lives on the server. Somebody pressed تم, and it is gone for
*             everybody. This is the real state of the work.
*   read    - lives in this browser. "I have seen this one." It only dims
*             the row and clears the badge; it changes nothing for anyone
*             else and never marks work as finished.
*
* Keeping read local is deliberate. A shared "read" flag would mean one
* person opening the bell clears the badge for the whole office, which is
* how a notification everybody assumed somebody else had handled gets
* missed. Seen-by-me is the honest claim a browser can make.
*/

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Badge from "@mui/material/Badge";
import Icon from "@mui/material/Icon";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { recordLink } from "utils/openRecord";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * How often the bell asks again while a screen is left open.
 *
 * Two minutes was too slow to be believed: a notice set to fire one minute
 * from now would sit there for another two before anyone saw it, and the
 * office read that as it not working. Twenty seconds costs one small request
 * and makes the arrival feel immediate.
 */
const POLL_MS = 20000;

const SEEN_KEY = "tc_notifications_seen";

/**
 * One stable name for a task, so "seen" survives a reload and a refetch.
 *
 * The two families are namespaced apart: a delivery-note reminder carries
 * both an order and a container, and keying it by the container would have
 * it share an identity with that container's own steps.
 */
const idOf = (row) =>
  row.order_id ? `order-${row.order_id}:${row.step}` : `container-${row.container_id}:${row.step}`;

const readSeen = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY));
    return Array.isArray(raw) ? new Set(raw) : new Set();
  } catch (e) {
    // a corrupted entry must not take the whole bar down
    return new Set();
  }
};

const writeSeen = (set) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...set]));
  } catch (e) {
    // private browsing, or a full quota - the bell still works, it just
    // forgets what was seen
  }
};

/**
 * How the office would say which shipment this is - the file number they use
 * out loud, the number on the box, and who is carrying it. Empty parts are
 * dropped: a dash is not an answer, and three of them are noise.
 */
const shipmentOf = (row) =>
  (row.order_id
    ? [row.shop_code, row.customer, row.goods]
    : [row.container_name, row.container_no, row.shipping_line]
  )
    .map((v) => String(v || "").trim())
    .filter((v) => v && v !== "0" && v !== "-")
    .join("  ·  ");

/**
 * The arrival sound - two soft notes, made in the browser rather than
 * shipped as a file, so nothing has to load before it can be heard.
 *
 * Browsers refuse to make sound until the page has been interacted with;
 * the context is created on the first click anywhere and resumed on every
 * chime, so the first notice after someone has touched the page is heard.
 */
function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;

    chime.ctx = chime.ctx || new Ctx();

    const { ctx } = chime;

    if (ctx.state === "suspended") ctx.resume();

    const start = ctx.currentTime;

    // a fifth apart, the second a beat later - a doorbell, not an alarm
    [880, 1318.5].forEach((hz, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const at = start + i * 0.17;

      osc.type = "sine";
      osc.frequency.value = hz;

      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.22, at + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.5);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.55);
    });
  } catch (e) {
    // a browser that will not make a sound is not an error worth showing
  }
}

/** How long a toast stays up before it takes itself away. */
const TOAST_MS = 9000;

/** How late something is, in the fewest words that still say it. */
function lateness(days) {
  if (days <= 0) return { text: "today", tone: "info" };
  if (days === 1) return { text: "1 day late", tone: "warning" };
  if (days <= 7) return { text: `${days} days late`, tone: "warning" };
  return { text: `${days} days late`, tone: "error" };
}

function NotificationBell() {
  const [due, setDue] = useState([]);
  const [seen, setSeen] = useState(readSeen);
  const [anchor, setAnchor] = useState(null);
  const [toasts, setToasts] = useState([]);
  const navigate = useNavigate();

  // so the poll never fights a request that is already in flight
  const loading = useRef(false);

  /**
   * What was already on the list last time we looked. The first answer only
   * fills this in - opening a screen is not an arrival, and announcing ten
   * old notices at once with a sound is how a person learns to ignore the
   * sound.
   */
  const known = useRef(null);

  /** Anything on the list that was not on the last one arrived just now. */
  const announce = (rows) => {
    const ids = new Set(rows.map(idOf));

    if (known.current === null) {
      known.current = ids;
      return;
    }

    const fresh = rows.filter((r) => !known.current.has(idOf(r)));
    known.current = ids;

    if (fresh.length === 0) return;

    chime();
    setToasts((t) => [...fresh, ...t].slice(0, 4));

    fresh.forEach((row) => {
      setTimeout(() => {
        setToasts((t) => t.filter((x) => idOf(x) !== idOf(row)));
      }, TOAST_MS);
    });
  };

  const load = async () => {
    if (loading.current) return;
    loading.current = true;

    try {
      /**
       * Both families of notice, in one bell. The order workflow had a
       * server, a settings screen and no reader - so a chase notice set to
       * fire in one minute fired into nothing.
       */
      const [containers, orders] = await Promise.all(
        [`${API}/container-tasks`, `${API}/order-tasks`].map((url) =>
          fetch(url, { cache: "no-store" })
            .then((r) => r.json())
            .then((j) => (Array.isArray(j.due) ? j.due : []))
            // one family being unavailable must not silence the other
            .catch(() => [])
        )
      );

      const rows = [...containers, ...orders].sort(
        (a, b) => (b.overdue_days || 0) - (a.overdue_days || 0)
      );

      setDue(rows);
      announce(rows);
    } catch (e) {
      // the bell is decoration on every other screen - a failure here must
      // never be what breaks the navigation bar
      setDue([]);
    }

    loading.current = false;
  };

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);

    /**
     * The first touch anywhere on the page is what buys the right to make a
     * sound later. Nothing is played here - the context is simply created
     * while a gesture is being handled, which is the only time a browser
     * will allow it.
     */
    const wake = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        chime.ctx = chime.ctx || (Ctx ? new Ctx() : null);
        if (chime.ctx && chime.ctx.state === "suspended") chime.ctx.resume();
      } catch (e) {
        // no audio on this browser - the toast still shows
      }
    };

    window.addEventListener("pointerdown", wake, { once: true });
    window.addEventListener("keydown", wake, { once: true });

    return () => {
      clearInterval(id);
      window.removeEventListener("pointerdown", wake);
      window.removeEventListener("keydown", wake);
    };
  }, []);

  const unread = due.filter((r) => !seen.has(idOf(r)));

  const markSeen = (rows) => {
    const next = new Set(seen);
    rows.forEach((r) => next.add(idOf(r)));
    setSeen(next);
    writeSeen(next);
  };

  const open = (e) => setAnchor(e.currentTarget);

  /**
   * A notice is about one order or one container, and a click on it opens
   * that record - its data, editable - rather than the list of all notices,
   * which only said the same thing again and left the order to be looked for.
   * Answering a notice (تم, تخطي) is still the notifications page, and
   * "Open all notifications" at the foot of the panel is still the way there.
   */
  const go = (row) => {
    markSeen([row]);
    setAnchor(null);
    navigate(recordLink(row));
  };

  return (
    <>
      {/**
       * What just arrived, said out loud in the corner of the screen.
       *
       * Pinned to the top right in both directions - this is where the bell
       * itself sits, so the eye that follows the sound lands on the thing
       * that explains it. Clicking one opens the order or container it is about.
       */}
      <SuiBox
        dir="rtl"
        sx={{
          position: "fixed",
          top: 76,
          right: 16,
          zIndex: 1500,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: "min(92vw, 380px)",
          pointerEvents: "none",
        }}
      >
        {toasts.map((row) => {
          const late = lateness(row.overdue_days);

          return (
            <SuiBox
              key={`toast-${idOf(row)}`}
              onClick={() => {
                setToasts((t) => t.filter((x) => idOf(x) !== idOf(row)));
                go(row);
              }}
              sx={({ palette, borders, boxShadows, functions: { rgba } }) => ({
                pointerEvents: "auto",
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 2,
                py: 1.5,
                backgroundColor: palette.white.main,
                borderRadius: borders.borderRadius.lg,
                boxShadow: boxShadows.xl,
                borderInlineStartWidth: 4,
                borderInlineStartStyle: "solid",
                borderInlineStartColor: palette[late.tone].main,
                animation: "tcToastIn 220ms ease-out",
                "@keyframes tcToastIn": {
                  from: { opacity: 0, transform: "translateY(-8px)" },
                  to: { opacity: 1, transform: "none" },
                },
                "&:hover": { backgroundColor: rgba(palette.dark.main, 0.03) },
              })}
            >
              <Icon sx={({ palette }) => ({ color: palette[late.tone].main })}>
                notifications_active
              </Icon>

              <SuiBox sx={{ minWidth: 0 }}>
                <SuiTypography sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.35 }}>
                  {row.label}
                </SuiTypography>
                <SuiTypography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    opacity: 0.8,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {shipmentOf(row)}
                </SuiTypography>
              </SuiBox>
            </SuiBox>
          );
        })}
      </SuiBox>

      <SuiBox
        component="button"
        type="button"
        onClick={open}
        aria-label={`Notifications${unread.length ? ` - ${unread.length} unread` : ""}`}
        sx={({ palette, borders, functions: { rgba } }) => ({
          display: "grid",
          placeItems: "center",
          width: 40,
          height: 40,
          flexShrink: 0,
          cursor: "pointer",
          border: "none",
          borderRadius: borders.borderRadius.md,
          backgroundColor: "transparent",
          color: rgba(palette.white.main, 0.75),
          transition: "background-color 160ms ease, color 160ms ease",
          "&:hover": {
            color: palette.primary.main,
            backgroundColor: rgba(palette.primary.main, 0.12),
          },
        })}
      >
        <Badge
          badgeContent={unread.length}
          max={99}
          color="error"
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": { fontWeight: 700, fontSize: 10.5, minWidth: 18, height: 18 },
          }}
        >
          <Icon sx={{ fontSize: "22px !important" }}>
            {unread.length ? "notifications_active" : "notifications"}
          </Icon>
        </Badge>
      </SuiBox>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // the page behind is dimmed, so this reads as one panel to answer
        // rather than as something floating over the screen
        sx={({ palette, functions: { rgba } }) => ({
          "& .MuiBackdrop-root": { backgroundColor: rgba(palette.dark.main, 0.25) },
        })}
        PaperProps={{
          // the notices are written in Arabic, so the panel is laid out in
          // Arabic: the notice starts at the right edge and its shipment or
          // order runs leftwards under it
          dir: "rtl",
          /**
           * Painted solid on purpose. The theme makes every popover paper
           * transparent, which left the list sitting on top of whatever page
           * was behind it - two sets of Arabic text over each other, and no
           * way to tell where the panel started or ended.
           */
          sx: ({ palette, borders, boxShadows, functions: { rgba } }) => ({
            width: { xs: 330, sm: 460 },
            maxHeight: 520,
            p: 0,
            backgroundColor: `${palette.white.main} !important`,
            backgroundImage: "none",
            border: `1px solid ${rgba(palette.dark.main, 0.12)}`,
            borderRadius: borders.borderRadius.lg,
            boxShadow: boxShadows.xl,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }),
        }}
      >
        <SuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1.5,
          }}
        >
          <SuiTypography sx={{ fontSize: 15, fontWeight: 800 }}>
            {`Notifications${due.length ? ` (${due.length})` : ""}`}
          </SuiTypography>

          {unread.length > 0 && (
            <Button size="small" onClick={() => markSeen(due)} sx={{ fontSize: 12 }}>
              Mark all read
            </Button>
          )}
        </SuiBox>

        <Divider sx={{ m: 0 }} />

        <SuiBox sx={{ overflowY: "auto", flex: 1 }}>
          {due.length === 0 && (
            <SuiBox sx={{ py: 5, textAlign: "center" }}>
              <Icon
                sx={({ palette }) => ({ fontSize: "34px !important", color: palette.success.main })}
              >
                check_circle
              </Icon>
              <SuiTypography sx={{ fontSize: 13.5, opacity: 0.7, mt: 1 }}>
                Nothing waiting
              </SuiTypography>
            </SuiBox>
          )}

          {due.map((row) => {
            const late = lateness(row.overdue_days);
            const isRead = seen.has(idOf(row));

            return (
              <SuiBox
                key={idOf(row)}
                onClick={() => go(row)}
                sx={({ palette, functions: { rgba } }) => ({
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  px: 2,
                  py: 1.4,
                  cursor: "pointer",
                  borderBottom: `1px solid ${rgba(palette.dark.main, 0.06)}`,
                  // unread carries its own weight and a mark; read is the
                  // quiet state, not a hidden one
                  backgroundColor: isRead ? "transparent" : rgba(palette.primary.main, 0.06),
                  "&:hover": { backgroundColor: rgba(palette.dark.main, 0.05) },
                })}
              >
                <SuiBox
                  sx={({ palette }) => ({
                    width: 8,
                    height: 8,
                    mt: 0.8,
                    flexShrink: 0,
                    borderRadius: "50%",
                    backgroundColor: isRead ? "transparent" : palette[late.tone].main,
                  })}
                />

                <SuiBox sx={{ minWidth: 0, flex: 1 }}>
                  {/* what to do, and how late - on one line so the eye does
                      not have to travel to the far edge and back */}
                  <SuiBox
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    <SuiTypography
                      sx={{ fontSize: 14, fontWeight: isRead ? 500 : 800, lineHeight: 1.35 }}
                    >
                      {row.label}
                    </SuiTypography>

                    <SuiTypography
                      sx={({ palette }) => ({
                        fontSize: 11.5,
                        fontWeight: 700,
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        color: palette[late.tone].main,
                      })}
                    >
                      {late.text}
                    </SuiTypography>
                  </SuiBox>

                  {/* which shipment, named rather than coded */}
                  <SuiTypography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      opacity: 0.85,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {shipmentOf(row)}
                  </SuiTypography>

                  {/* and why it is being asked for today */}
                  {row.because && (
                    <SuiTypography
                      sx={{
                        fontSize: 11.5,
                        opacity: 0.6,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.because}
                    </SuiTypography>
                  )}
                </SuiBox>
              </SuiBox>
            );
          })}
        </SuiBox>

        <Divider sx={{ m: 0 }} />

        <SuiBox sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={() => {
              setAnchor(null);
              navigate("/notifications");
            }}
            sx={{ fontSize: 13, fontWeight: 700 }}
          >
            Open all notifications
          </Button>
        </SuiBox>
      </Popover>
    </>
  );
}

export default NotificationBell;
